import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox.tsx";
import Table from "@via/components/table/table.tsx";
import { TruncatedText } from "@via/components/truncated-text";
import { GeoItem } from "common";
import { JSX, useEffect, useState } from "react";

const columnHelper = createColumnHelper<GeoItem>();

export default function GeoFileTable({
  isLoading,
  geoList,
  onAddToSelected,
  total = 0,
}: {
  isLoading: boolean;
  geoList: GeoItem[];
  onAddToSelected: (selectedItems: GeoItem[]) => void;
  total: number;
}): JSX.Element {
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [selectedGeoItems, setSelectedGeoItems] = useState<GeoItem[]>([]);
  const [currentPageSize, setCurrentPageSize] = useState(50);

  const allRowsSelected =
    selectedGeoItems.length > 0 && selectedGeoItems.length === geoList.length;
  const someRowsSelected = selectedGeoItems.length > 0 && !allRowsSelected;

  const toggleAllRowsSelected = (): void => {
    setSelectedGeoItems(allRowsSelected ? [] : geoList);
  };

  const handleCheckboxToggle = (row: GeoItem): void => {
    setSelectedGeoItems(prev =>
      prev.some(({ srrId }) => srrId === row.srrId)
        ? prev.filter(({ srrId }) => srrId !== row.srrId)
        : [...prev, row]
    );
  };

  useEffect(() => {
    if (shouldScrollToBottom) {
      const viaWrapper = document.querySelector(
        ".via-view-wrapper"
      ) as HTMLElement;
      if (viaWrapper) {
        viaWrapper.scrollTo({
          top: viaWrapper.scrollHeight,
          behavior: "smooth",
        });
        setShouldScrollToBottom(false);
      }
    }
  }, [shouldScrollToBottom]);

  const handleAddSelectedFiles = (): void => {
    if (selectedGeoItems.length) {
      const updatedSelectedGeoItems = selectedGeoItems.map(item => {
        return {
          ...item,
          collectionType: item.collectionType === "Single" ? "single" : "pair",
        };
      });
      onAddToSelected(updatedSelectedGeoItems);
      setSelectedGeoItems([]);
      setShouldScrollToBottom(true);
    }
  };

  const columns = [
    columnHelper.accessor("srrId", {
      header: () => (
        <IndeterminateCheckbox
          checked={allRowsSelected}
          indeterminate={someRowsSelected}
          onChange={toggleAllRowsSelected}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckbox
          checked={selectedGeoItems.some(
            ({ srrId }) => srrId === row.original.srrId
          )}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={() => {
            row.toggleSelected();
            handleCheckboxToggle(row.original);
          }}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      ),
    }),
    columnHelper.accessor("name", {
      cell: info => (
        <TruncatedText
          text={info.getValue()}
          maxLength={70}
          id={`tooltip-${info.column.id}-${info.row.original.srrId}`}
        />
      ),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("srrId", {
      cell: info => info.getValue(),
      header: () => <span>GEO Accession</span>,
    }),
    columnHelper.accessor("collectionType", {
      cell: info => info.getValue(),
      header: () => <span>Single / Pair</span>,
    }),
  ] as Array<ColumnDef<GeoItem>>;

  return (
    <div>
      <h2 className="via-h2 text-lg font-bold">Results</h2>

      <Table
        data={geoList || []}
        columns={columns}
        total={total}
        onRowClick={handleCheckboxToggle}
        isLoading={isLoading}
        pageSize={currentPageSize}
        setPageSize={setCurrentPageSize}
        pageSizeOptions={[10, 20, 50]}
      />

      {geoList.length ? (
        <div>
          <div className="flex flex-shrink-0 justify-end py-4">
            <button
              data-testid="geoContinueButton"
              disabled={isLoading}
              onClick={handleAddSelectedFiles}
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Continue with selected files
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
